export const TEMPLATE_VERSION = "fifa-journey-v1";

export const TEAM_OPTIONS = [
  { code: "BRA", label: "Brasil", flag: "🇧🇷" },
  { code: "ARG", label: "Argentina", flag: "🇦🇷" },
  { code: "POR", label: "Portugal", flag: "🇵🇹" },
  { code: "FRA", label: "França", flag: "🇫🇷" },
  { code: "ESP", label: "Espanha", flag: "🇪🇸" },
  { code: "MEX", label: "México", flag: "🇲🇽" }
] as const;

export const POSITION_OPTIONS = [
  { code: "atacante", label: "Atacante" },
  { code: "meio-campo", label: "Meio-campo" },
  { code: "defensor", label: "Defensor" },
  { code: "goleiro", label: "Goleiro" }
] as const;

export type TeamCode = (typeof TEAM_OPTIONS)[number]["code"];
export type PositionCode = (typeof POSITION_OPTIONS)[number]["code"];

export interface PlayerProfile {
  playerName: string;
  shirtNumber: number;
  position: PositionCode;
  teamCode: TeamCode;
  kit: "home" | "away";
}

export interface SlotState {
  slot: number;
  kind: "preview" | "surprise";
  status: "queued" | "running" | "retry_wait" | "succeeded" | "failed";
  title: string;
  assetId?: string;
}

export const SLOT_TITLES = [
  "A Convocação",
  "Pronto para o Jogo",
  "O Túnel",
  "A Estreia",
  "Mata-mata",
  "Noite Decisiva",
  "O Gol",
  "A Glória"
] as const;

const SCENES = [
  "formal player portrait inside a professional locker room before the match, confident expression, medium close-up",
  "professional football player holding a football at chest height, editorial half-body portrait",
  "walking alone through the player tunnel toward a packed world championship stadium, cinematic backlight",
  "hero portrait on the pitch under dramatic night stadium floodlights, medium shot",
  "focused knockout-match preparation, seated and tying football boots, intimate pre-game moment",
  "decisive night match atmosphere on the touchline, cinematic sports photography, controlled movement",
  "personal goal celebration with raised arms, emotional but face clearly visible, stadium background",
  "champion cover portrait holding the world football trophy, golden confetti, iconic victory lighting"
] as const;

export function buildGenerationPrompt(slot: number, profile: PlayerProfile): string {
  const scene = SCENES[slot - 1];
  if (!scene) throw new Error(`Invalid generation slot: ${slot}`);
  const kit = profile.kit === "home" ? "home" : "away";
  return [
    "Create a photorealistic premium football editorial photograph using the provided person as the identity reference.",
    `Scene: ${scene}.`,
    `The person is a ${profile.position} representing ${profile.teamCode}, wearing the current ${kit} national team kit.`,
    "Preserve the exact facial identity, apparent age, skin tone, hairstyle, facial hair and natural proportions from the reference photo.",
    "Single person as the clear subject, face sharp and recognizable, realistic hands, realistic sports fabric, vertical 4:5 composition.",
    "World championship atmosphere, premium sports magazine photography, natural skin texture, cinematic but believable lighting.",
    "Do not add written names, captions or random text."
  ].join(" ");
}

export function normalizeProfile(input: Partial<PlayerProfile>): PlayerProfile {
  const teamCodes = new Set(TEAM_OPTIONS.map((item) => item.code));
  const positions = new Set(POSITION_OPTIONS.map((item) => item.code));
  const rawName = typeof input.playerName === "string" ? input.playerName.trim() : "";
  return {
    playerName: (rawName || "CRAQUE").slice(0, 18).toUpperCase(),
    shirtNumber: Number.isInteger(input.shirtNumber)
      ? Math.max(0, Math.min(99, Number(input.shirtNumber)))
      : 10,
    position: positions.has(input.position as PositionCode) ? input.position as PositionCode : "atacante",
    teamCode: teamCodes.has(input.teamCode as TeamCode) ? input.teamCode as TeamCode : "BRA",
    kit: input.kit === "away" ? "away" : "home"
  };
}
